import React, { useState } from "react";
import { Box, IconButton, Menu, MenuItem, Typography, useTheme } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import {
  deleteMultipleReviewer,
  deleteReviewer,
  updateIndividualReviewer,
} from "redux/appSlice";
import { normalizeReviewerOrders } from "utils/signerOrderUtils";
import { isTruthy } from "helpers/methods";
import strings from "global/constants/StringConstants";
import { Reviewers } from "models/interfaces";
import CustomInput from "global/components/CustomInput/CustomInput";
import MobileContactAutocomplete from "./MobileContactAutocomplete";
import { MobileSignRequestStyles } from "../MobileSignRequest.styles";
import { pressableProps } from "../mobileA11y";

interface MobileApproverGroupCardProps {
  members: Reviewers[];
  displayIndex: number;
  allReviewers: Reviewers[];
  excludeIds: string[];
  setExcludeIds: Function;
  contactList: any;
  setContactList: Function;
  onAddMember: (groupName: string) => void;
}

const MobileApproverGroupCard: React.FC<MobileApproverGroupCardProps> = ({
  members,
  displayIndex,
  allReviewers,
  excludeIds,
  setExcludeIds,
  contactList,
  setContactList,
  onAddMember,
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const classes = MobileSignRequestStyles(theme);
  const dispatch = useAppDispatch();
  const errors = useAppSelector((s: RootState) => s.app.errors);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const groupName = members[0]?.group || "";
  const groupId = members[0]?.groupId;
  const groupColor = members[0]?.color || m.ePurple[500];

  const handleGroupNameChange = (value: string) => {
    const trimmed = value.trimStart().replace(/(?:^\s+|\s+$|\t+)/g, " ");
    members.forEach((mem) =>
      dispatch(
        updateIndividualReviewer({
          reviewerId: mem.id,
          fieldsToUpdate: { group: trimmed },
        }),
      ),
    );
  };

  const handleDeleteGroup = () => {
    const remaining = allReviewers.filter((r) => r.groupId !== groupId);
    dispatch(deleteMultipleReviewer(normalizeReviewerOrders(remaining)));
    setMenuAnchor(null);
  };

  const handleDeleteMember = (member: Reviewers) => {
    // Deleting the last member removes the whole group.
    if (members.length <= 1) {
      handleDeleteGroup();
      return;
    }
    dispatch(deleteReviewer({ reviewerId: member.id }));
    if (member.email) {
      setExcludeIds(excludeIds.filter((id: string) => id !== member.email));
    }
  };

  return (
    <Box sx={classes.signerCard}>
      <Box sx={classes.signerCardHead}>
        <Box sx={classes.signerCardHeadLeft}>
          <Box aria-hidden="true" sx={classes.signerBadge(groupColor)}>
            {displayIndex + 1}
          </Box>
          <Typography sx={classes.signerCardTitle}>Group</Typography>
        </Box>
        <IconButton
          size="small"
          aria-label={`Options for group ${displayIndex + 1}`}
          aria-haspopup="menu"
          aria-expanded={Boolean(menuAnchor)}
          aria-controls={menuAnchor ? `group-${groupId}-menu` : undefined}
          sx={{ p: 0.5, color: m.base[500] }}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          id={`group-${groupId}-menu`}
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          MenuListProps={{ "aria-label": "Group options" }}
        >
          <MenuItem onClick={handleDeleteGroup}>
            <Typography variant="subtitle2">Delete</Typography>
          </MenuItem>
        </Menu>
      </Box>

      {members.map((member, i) => (
        <Box key={member.id} sx={classes.approverMemberBox(groupColor)}>
          <Box sx={classes.approverMemberHead}>
            <Typography sx={classes.approverMemberLabel}>
              Approver {i + 1}
            </Typography>
            <IconButton
              size="small"
              aria-label={`Delete approver ${i + 1}`}
              sx={{ p: 0, color: m.base[500] }}
              onClick={() => handleDeleteMember(member)}
            >
              <DeleteOutlineIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Box>
            <Typography
              component="label"
              htmlFor={`group-name-${member.id}`}
              sx={classes.fieldLabel}
            >
              Group Name
            </Typography>
            <CustomInput
              id={`group-name-${member.id}`}
              placeHolder="Enter group name"
              type="text"
              name="group"
              value={groupName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleGroupNameChange(e.target.value)
              }
              error={!isTruthy(groupName) && errors.group}
              sx={{
                "& .MuiOutlinedInput-input": { padding: "0.4375rem" },
                "& .MuiOutlinedInput-root": {
                  border: "none !important",
                  padding: "0.4375rem !important",
                  height: "2.5rem",
                  borderRadius: "0.5rem",
                  backgroundColor: m.surface.white,
                },
                "& .MuiInputBase-input": { padding: "0.4375rem !important" },
              }}
            />
          </Box>

          <MobileContactAutocomplete
            field="name"
            id={`approver-${member.id}-name`}
            label="Full Name"
            placeholder="Enter full name"
            value={member.name || ""}
            index={member.id}
            currentEmail={member.email || ""}
            contactList={contactList}
            setContactList={setContactList}
            excludeIds={excludeIds}
            setExcludeIds={setExcludeIds}
            onUpdate={(fields) =>
              dispatch(
                updateIndividualReviewer({
                  reviewerId: member.id,
                  fieldsToUpdate: fields,
                }),
              )
            }
            error={!isTruthy(member.name) && errors.name}
            errorMessage={
              !isTruthy(member.name) && errors.name ? "Name is required" : ""
            }
          />

          <MobileContactAutocomplete
            field="email"
            id={`approver-${member.id}-email`}
            label="Email Address"
            placeholder="Enter email address"
            value={member.email || ""}
            index={member.id}
            currentEmail={member.email || ""}
            contactList={contactList}
            setContactList={setContactList}
            excludeIds={excludeIds}
            setExcludeIds={setExcludeIds}
            onUpdate={(fields) =>
              dispatch(
                updateIndividualReviewer({
                  reviewerId: member.id,
                  fieldsToUpdate: fields,
                }),
              )
            }
            error={
              (!isTruthy(member.email) || !strings.regex.test(member.email)) &&
              errors.email
            }
          />
        </Box>
      ))}

      <Box
        component="span"
        sx={{ ...classes.addDelayLink, mt: 0.5 }}
        {...pressableProps(() => onAddMember(groupName), { label: "Add member" })}
      >
        <AddIcon sx={{ fontSize: 18 }} />
        Add Member
      </Box>
    </Box>
  );
};

export default MobileApproverGroupCard;
